import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { listHunts, getHuntStats, getMyHunts } from '../services/api';
import { HuntCard } from '../components/hunt/HuntCard';
import { Countdown } from '../components/common/Countdown';
import { LiveStats } from '../components/hunt/LiveStats';
import { Crane } from '../components/mascot/Crane';
import { useAuthStore } from '../stores/authStore';

export function LandingPage() {
  const { isAuthenticated } = useAuthStore();

  const { data, isLoading, error } = useQuery({
    queryKey: ['hunts'],
    queryFn: listHunts,
  });

  const { data: myHunts } = useQuery({
    queryKey: ['myHunts'],
    queryFn: getMyHunts,
    enabled: isAuthenticated,
  });

  const activeHunt = data?.hunts.find(h => h.status === 'live' || h.status === 'scheduled');

  const { data: stats } = useQuery({
    queryKey: ['huntStats', activeHunt?.huntId],
    queryFn: () => getHuntStats(activeHunt!.huntId),
    enabled: !!activeHunt && activeHunt.status === 'live',
    refetchInterval: 30000,
  });

  // Build a map of hunt progress for easy lookup
  const progressMap = new Map(
    myHunts?.hunts.map(h => [h.huntId, h]) || []
  );

  if (isLoading) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <div className="animate-pulse text-text-light">Loading hunts...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <div className="text-red-600">Failed to load hunts</div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-12">
      {/* Hero Section */}
      <section className="text-center mb-16 animate-fade-in-up">
        <div className="mb-6 flex justify-center">
          <Crane pose="idle" size="lg" />
        </div>
        <h1 className="text-4xl md:text-5xl font-bold text-primary mb-4">
          CloudFruit Hunts
        </h1>
        <p className="text-xl text-text-light max-w-2xl mx-auto mb-4">
          Digital scavenger hunts with real cash prizes.
        </p>
        <p className="text-lg text-text-light max-w-xl mx-auto mb-8">
          Each hunt features unique themes, challenging puzzles, and rewards for the fastest solvers.
        </p>

        {activeHunt && (
          <div className="card max-w-xl mx-auto">
            {activeHunt.status === 'scheduled' && (
              <div>
                <h2 className="text-lg font-semibold text-text-dark mb-2">
                  Next Hunt: {activeHunt.title}
                </h2>
                <Countdown targetDate={activeHunt.launchAt} />
                <p className="text-accent font-semibold mt-4">
                  ${activeHunt.prizes.reduce((sum, p) => sum + p.amount, 0)} in prizes
                </p>
              </div>
            )}

            {activeHunt.status === 'live' && stats && (
              <div>
                <h2 className="text-lg font-semibold text-text-dark mb-4">
                  <span className="badge-live mr-2">LIVE</span>
                  {activeHunt.title}
                </h2>
                <LiveStats stats={stats} />
                <Link to={`/h/${activeHunt.huntId}`} className="btn-primary mt-4 inline-block">
                  Start Hunting
                </Link>
              </div>
            )}
          </div>
        )}
      </section>

      {/* Hunt Grid */}
      <section>
        <h2 className="text-2xl font-bold text-primary mb-6">All Hunts</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {data?.hunts.map(hunt => (
            <HuntCard
              key={hunt.huntId}
              hunt={hunt}
              userProgress={progressMap.get(hunt.huntId)}
              isAuthenticated={isAuthenticated}
            />
          ))}
        </div>
      </section>
    </div>
  );
}
